import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CAlert, CButton, CCard, CCardBody, CCardHeader, CSpinner, CStepper } from "@coreui/react-pro";
import { canResumeMembership, useMyMemberships, useResumeMembership } from "../../api/hooks/useMemberships";
import type { ApiError } from "../../api/client";
import { useFailure } from "../../context/useFailure";
import { useToast } from "../../context/useToast";
import { membershipDetailsPath, routePaths } from "../../routes/route-paths";
import { formatDateShort } from "../../lib/dates";

// CStepper's exposed imperative API. Mirrors the End wizard so the two flows stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

export function MembershipResumeWizard() {
  const navigate = useNavigate();
  const { tierId = "" } = useParams<{ tierId: string }>();

  const memberships = useMyMemberships();
  const resumeRemote = useResumeMembership();
  const { showSuccess } = useToast();
  const { showFailure, clearFailure } = useFailure();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Only the customer's own cancel that has not yet taken effect can be resumed — the same rule the server enforces.
  const membership = memberships.data?.find((m) => m.membershipTierId === tierId && canResumeMembership(m));

  if (memberships.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (memberships.error) {
    return (
      <div>
        <CAlert color="danger">Unable to load your membership. Please try again.</CAlert>
      </div>
    );
  }

  if (!membership) {
    return (
      <div>
        <CAlert color="warning">
          There's no cancelled membership to resume for this tier.{" "}
          <Link to={routePaths.memberships}>Back to memberships</Link>.
        </CAlert>
      </div>
    );
  }

  const name = `${membership.platformName} · ${membership.tierName}`;
  const detailPath = membershipDetailsPath(tierId);

  const handleNext = () => stepperRef.current?.next();

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(detailPath);
    } else {
      stepperRef.current?.prev();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    clearFailure();
    try {
      await resumeRemote.mutateAsync(tierId);
      showSuccess(`${name} resumed.`);
      navigate(detailPath);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to resume this membership. Please try again.";
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      label: "Details",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Details</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              You're about to resume the following membership. Review the details, then continue to confirm.
            </p>
            <dl className="row mb-4">
              <dt className="col-bo-3 text-body-secondary fw-normal">Membership</dt>
              <dd className="col-bo-9 fw-semibold">{name}</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Status</dt>
              <dd className="col-bo-9">Ending</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Access until</dt>
              <dd className="col-bo-9">{formatDateShort(membership.activeTo!)}</dd>
            </dl>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleNext}>
                Next: Review
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <CAlert color="success">
              This membership was cancelled, with access set to end on {formatDateShort(membership.activeTo!)}.
              Resuming reverses that.
            </CAlert>
            <p>When you confirm:</p>
            <ul className="mb-4">
              <li>
                Your <strong>{name}</strong> membership continues with no end date.
              </li>
              <li>Your access to the strategies it includes continues uninterrupted.</li>
              <li>
                Billing continues on your normal cycle. <Link to={routePaths.billing}>Billing</Link> shows what you'll
                pay.
              </li>
            </ul>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack} disabled={submitting}>
                Back
              </CButton>
              <CButton color="success" onClick={handleSubmit} disabled={submitting}>
                {submitting && <CSpinner size="sm" className="me-2" />}
                Resume membership
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>Resume membership</h2>
      <p className="text-body-secondary">
        Continue your <strong>{name}</strong> membership.
      </p>
      <hr />

      <CStepper ref={stepperRef} steps={steps} onStepChange={setCurrentStep} onReset={() => setCurrentStep(1)} />
    </>
  );
}

export default MembershipResumeWizard;
